import Spinner from '@/components/layout/Spinner/Spinner';
import useLoginModal from '@/hooks/useLoginModal';
import useUser from '@/hooks/useUser';
import { AccountTierType, BillingPeriodType } from '@/proxies/UserProxy';
import type { AppConfigPriceType } from '@/redux/ducks/ConfigDucks.types';
import type { AppSsrFetchedDataType } from '@/server/AppSsr';
import { isEmpty, isNil } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import type React from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import TierCard from './TierCard';

export type TierFeatureType = {
  id: string;
  tier: AccountTierType;
  included?: boolean;
  highlight?: boolean;
  selected?: boolean;
};

interface PricesProps {
  ssrFetchedData: AppSsrFetchedDataType;
}

export const DEFAULT_TIER_CURRENCY: string = 'EUR';

const TiersSection: React.FC<PricesProps> = ({ ssrFetchedData }) => {
  const { t } = useTranslation();
  const loginModal = useLoginModal();
  const { currentUser, userCurrency } = useUser();

  const freePlanFeatures = useMemo<TierFeatureType[]>(() => {
    return [
      {
        id: '1',
        tier: AccountTierType.Free,
        included: true,
        highlight: false,
        selected: false,
      },
      {
        id: '2',
        tier: AccountTierType.Free,
        included: true,
        highlight: false,
        selected: false,
      },
      {
        id: '3',
        tier: AccountTierType.Free,
        included: true,
        highlight: false,
        selected: false,
      },
      {
        id: '4',
        tier: AccountTierType.Free,
        included: true,
        highlight: false,
        selected: false,
      },
      {
        id: '5',
        tier: AccountTierType.Free,
        included: true,
        highlight: false,
        selected: false,
      },
      {
        id: '6',
        tier: AccountTierType.Free,
        included: true,
        highlight: false,
        selected: false,
      },
      {
        id: '7',
        tier: AccountTierType.Free,
        included: true,
        highlight: false,
        selected: false,
      },
      {
        id: '8',
        tier: AccountTierType.Free,
        included: true,
        highlight: false,
        selected: false,
      },
      {
        id: '9',
        tier: AccountTierType.Free,
        included: true,
        highlight: false,
        selected: false,
      },
      {
        id: '10',
        tier: AccountTierType.Free,
        included: true,
        highlight: false,
        selected: false,
      },
      {
        id: '11',
        tier: AccountTierType.Free,
        included: true,
        highlight: false,
        selected: false,
      },
    ];
  }, []);

  const proPlanFeatures = useMemo<TierFeatureType[]>(() => {
    return [
      {
        id: '1',
        tier: AccountTierType.Pro,
        included: true,
        highlight: true,
        selected: false,
      },
      {
        id: '2',
        tier: AccountTierType.Pro,
        included: true,
        highlight: true,
        selected: false,
      },
      {
        id: '3',
        tier: AccountTierType.Pro,
        included: true,
        highlight: true,
        selected: false,
      },
      {
        id: '4',
        tier: AccountTierType.Pro,
        included: true,
        highlight: true,
        selected: false,
      },
      {
        id: '5',
        tier: AccountTierType.Pro,
        included: true,
        highlight: true,
        selected: false,
      },
      {
        id: '6',
        tier: AccountTierType.Pro,
        included: true,
        highlight: false,
        selected: false,
      },
      {
        id: '7',
        tier: AccountTierType.Pro,
        included: true,
        highlight: false,
        selected: false,
      },
      {
        id: '8',
        tier: AccountTierType.Pro,
        included: true,
        highlight: false,
        selected: false,
      },
      {
        id: '9',
        tier: AccountTierType.Pro,
        included: true,
        highlight: false,
        selected: false,
      },
      {
        id: '10',
        tier: AccountTierType.Pro,
        included: true,
        highlight: false,
        selected: false,
      },
      {
        id: '11',
        tier: AccountTierType.Pro,
        included: true,
        highlight: false,
        selected: false,
      },
    ];
  }, []);

  const unlimitedPlanFeatures = useMemo<TierFeatureType[]>(() => {
    return [
      {
        id: '1',
        tier: AccountTierType.Unlimited,
        included: true,
        highlight: true,
        selected: true,
      },
      {
        id: '2',
        tier: AccountTierType.Unlimited,
        included: true,
        highlight: true,
        selected: true,
      },
      {
        id: '3',
        tier: AccountTierType.Unlimited,
        included: true,
        highlight: false,
        selected: true,
      },
      {
        id: '4',
        tier: AccountTierType.Unlimited,
        included: true,
        highlight: false,
        selected: true,
      },
      {
        id: '5',
        tier: AccountTierType.Unlimited,
        included: true,
        highlight: false,
        selected: true,
      },
      {
        id: '6',
        tier: AccountTierType.Unlimited,
        included: true,
        highlight: false,
        selected: true,
      },
      {
        id: '7',
        tier: AccountTierType.Unlimited,
        included: true,
        highlight: false,
        selected: true,
      },
      {
        id: '8',
        tier: AccountTierType.Unlimited,
        included: true,
        highlight: false,
        selected: true,
      },
      {
        id: '9',
        tier: AccountTierType.Unlimited,
        included: true,
        highlight: false,
        selected: true,
      },
      {
        id: '10',
        tier: AccountTierType.Unlimited,
        included: true,
        highlight: false,
        selected: true,
      },
      {
        id: '11',
        tier: AccountTierType.Unlimited,
        included: true,
        highlight: false,
        selected: true,
      },
    ];
  }, []);

  const [onMonthlyView, setOnMonthlyView] = useState<boolean>(false);

  const userTier = useMemo<AccountTierType | undefined>(() => {
    if (!isNil(currentUser)) {
      return currentUser.account_tier;
    }

    return undefined;
  }, [currentUser]);

  const switchView = useCallback(() => {
    setOnMonthlyView(!onMonthlyView);
  }, [onMonthlyView]);

  const onPlanClick = useCallback(
    (plan: AccountTierType) => {
      if (!isNil(currentUser)) {
        const targetPlan = ssrFetchedData.appConfiguration.prices.filter(
          (p) =>
            p.tier === plan &&
            ((p.billing === BillingPeriodType.Monthly &&
              onMonthlyView === true) ||
              (p.billing === BillingPeriodType.Yearly &&
                onMonthlyView !== true)),
        );
        if (
          targetPlan.length > 0 &&
          !isNil(targetPlan[0].stripe?.url) &&
          typeof window !== 'undefined'
        ) {
          const paymentLink = targetPlan[0].stripe.url;
          const targetUrl = `${paymentLink}?client_reference_id=${ssrFetchedData.domain}_${currentUser.id}`;

          window.open(targetUrl, '_blank');
        } else {
          toast.error(t('static.prices.unexpected_error_label'));
        }
      } else {
        loginModal.onOpen();
      }
    },
    [ssrFetchedData, onMonthlyView, currentUser, loginModal, t],
  );

  const userPrices = useMemo<AppConfigPriceType[] | undefined>(() => {
    if (isNil(ssrFetchedData?.appConfiguration?.prices)) {
      return undefined;
    }

    const defaultPrices = ssrFetchedData.appConfiguration.prices.filter(
      (p) => p.currency === DEFAULT_TIER_CURRENCY,
    );
    if (defaultPrices.length < 6) {
      return undefined;
    }

    const yearlyPriceFreeDefault = defaultPrices.filter(
      (p) =>
        p.billing === BillingPeriodType.Yearly &&
        p.tier === AccountTierType.Free,
    );
    const monthlyPriceFreeDefault = defaultPrices.filter(
      (p) =>
        p.billing === BillingPeriodType.Monthly &&
        p.tier === AccountTierType.Free,
    );
    const yearlyPriceProDefault = defaultPrices.filter(
      (p) =>
        p.billing === BillingPeriodType.Yearly &&
        p.tier === AccountTierType.Pro,
    );
    const monthlyPriceProDefault = defaultPrices.filter(
      (p) =>
        p.billing === BillingPeriodType.Monthly &&
        p.tier === AccountTierType.Pro,
    );
    const yearlyPriceUnlimitedDefault = defaultPrices.filter(
      (p) =>
        p.billing === BillingPeriodType.Yearly &&
        p.tier === AccountTierType.Unlimited,
    );
    const monthlyPriceUnlimitedDefault = defaultPrices.filter(
      (p) =>
        p.billing === BillingPeriodType.Monthly &&
        p.tier === AccountTierType.Unlimited,
    );

    const missingDefaultTierPrice =
      isEmpty(yearlyPriceFreeDefault) ||
      isEmpty(monthlyPriceFreeDefault) ||
      isEmpty(yearlyPriceProDefault) ||
      isEmpty(monthlyPriceProDefault) ||
      isEmpty(yearlyPriceUnlimitedDefault) ||
      isEmpty(monthlyPriceUnlimitedDefault);

    if (missingDefaultTierPrice) {
      return undefined;
    }

    if (!isNil(userCurrency)) {
      const userPrices = ssrFetchedData.appConfiguration.prices.filter(
        (p) => p.currency === userCurrency,
      );
      if (userPrices.length >= 6) {
        const yearlyPriceFreeUser = userPrices.filter(
          (p) =>
            p.billing === BillingPeriodType.Yearly &&
            p.tier === AccountTierType.Free,
        );
        const monthlyPriceFreeUser = userPrices.filter(
          (p) =>
            p.billing === BillingPeriodType.Monthly &&
            p.tier === AccountTierType.Free,
        );
        const yearlyPriceProUser = userPrices.filter(
          (p) =>
            p.billing === BillingPeriodType.Yearly &&
            p.tier === AccountTierType.Pro,
        );
        const monthlyPriceProUser = userPrices.filter(
          (p) =>
            p.billing === BillingPeriodType.Monthly &&
            p.tier === AccountTierType.Pro,
        );
        const yearlyPriceUnlimitedUser = userPrices.filter(
          (p) =>
            p.billing === BillingPeriodType.Yearly &&
            p.tier === AccountTierType.Unlimited,
        );
        const monthlyPriceUnlimitedUser = userPrices.filter(
          (p) =>
            p.billing === BillingPeriodType.Monthly &&
            p.tier === AccountTierType.Unlimited,
        );

        const missingUserTierPrice =
          isEmpty(yearlyPriceFreeUser) ||
          isEmpty(monthlyPriceFreeUser) ||
          isEmpty(yearlyPriceProUser) ||
          isEmpty(monthlyPriceProUser) ||
          isEmpty(yearlyPriceUnlimitedUser) ||
          isEmpty(monthlyPriceUnlimitedUser);

        if (!missingUserTierPrice) {
          return [
            monthlyPriceFreeUser[0],
            yearlyPriceFreeUser[0],
            monthlyPriceProUser[0],
            yearlyPriceProUser[0],
            monthlyPriceUnlimitedUser[0],
            yearlyPriceUnlimitedUser[0],
          ];
        }
      }
    }

    return [
      monthlyPriceFreeDefault[0],
      yearlyPriceFreeDefault[0],
      monthlyPriceProDefault[0],
      yearlyPriceProDefault[0],
      monthlyPriceUnlimitedDefault[0],
      yearlyPriceUnlimitedDefault[0],
    ];
  }, [ssrFetchedData, userCurrency]);

  return !isNil(userPrices) ? (
    <div className="min-w-full flex flex-col gap-16 p-2">
      <div className="flex flex-col items-center justify-center">
        <span className="rounded-full bg-white border-slate-200 border grid grid-cols-2 gap-0 text-sm font-bold drop-shadow-sm">
          <button
            type="button"
            className={`cursor-pointer p-1 px-3 rounded-full min-w-full text-center ${onMonthlyView ? 'bg-white text-inherit' : 'bg-sky-600 text-white'}`}
            onClick={switchView}
            disabled={onMonthlyView !== true}
          >
            {t('static.prices.payment_period_selector_yearly_label')}{' '}
            <span
              className={
                onMonthlyView === true ? 'text-slate-600' : 'text-slate-300'
              }
            >
              {t('static.prices.payment_period_selector_yearly_discount_label')}
            </span>
          </button>
          <button
            type="button"
            className={`cursor-pointer p-1 px-3 rounded-full min-w-full text-center ${onMonthlyView === true ? 'bg-sky-600 text-white' : 'bg-white text-inherit'}`}
            onClick={switchView}
            disabled={onMonthlyView === true}
          >
            {t('static.prices.payment_period_selector_monthly_label')}
          </button>
        </span>
      </div>
      <div className="min-w-full grid grid-cols-1 lg:grid-cols-3 justify-center items-center gap-8">
        <TierCard
          tier={AccountTierType.Free}
          monthly_price={userPrices[0].price}
          yearly_price={userPrices[1].price}
          currency={userPrices[1].currency}
          features={freePlanFeatures}
          display_monthly_price={onMonthlyView === true}
          userTier={userTier}
          onPlanClick={onPlanClick}
        />
        <TierCard
          tier={AccountTierType.Pro}
          selected
          popular
          monthly_price={userPrices[2].price}
          yearly_price={userPrices[3].price}
          currency={userPrices[3].currency}
          features={proPlanFeatures}
          display_monthly_price={onMonthlyView === true}
          userTier={userTier}
          onPlanClick={onPlanClick}
        />
        <TierCard
          tier={AccountTierType.Unlimited}
          monthly_price={userPrices[4].price}
          yearly_price={userPrices[5].price}
          currency={userPrices[5].currency}
          features={unlimitedPlanFeatures}
          display_monthly_price={onMonthlyView === true}
          userTier={userTier}
          onPlanClick={onPlanClick}
        />
      </div>
    </div>
  ) : (
    <div className="min-w-full flex flex-col p-2">
      <Spinner color="fill-sky-500" />
    </div>
  );
};

export default TiersSection;
